import type { ResourceItem, ResourcesStorage } from '../core/types';

const cache = new Map<string, ResourceItem[]>();
const listeners = new Map<string, Set<(items: ResourceItem[]) => void>>();

function normalizeHttpUrl(url: string): string {
  const raw = String(url || '').trim();
  if (!raw) throw new Error('Enter a URL');
  const next = /^https?:\/\//i.test(raw) ? raw : `https://${raw}`;
  const parsed = new URL(next);
  if (parsed.protocol !== 'https:' && parsed.protocol !== 'http:') throw new Error('Only http(s) URLs are allowed');
  return parsed.toString();
}

function getList(presId: string): ResourceItem[] {
  return cache.get(String(presId)) ?? [];
}

function setList(presId: string, items: ResourceItem[]): void {
  const key = String(presId);
  const sorted = items.slice().sort((a, b) => a.order - b.order);
  cache.set(key, sorted);
  const subs = listeners.get(key);
  if (subs) subs.forEach((cb) => cb(sorted.slice()));
}

export const resourcesStorageStub: ResourcesStorage = {
  getResources(presId) {
    return getList(presId);
  },
  getAIState() {
    return {};
  },

  subscribeResources(presId, cb) {
    const key = String(presId);
    if (!listeners.has(key)) listeners.set(key, new Set());
    listeners.get(key)!.add(cb);
    cb(getList(presId));
    return () => {
      listeners.get(key)?.delete(cb);
    };
  },

  subscribeAIState(_presId, cb) {
    cb({});
    return () => {};
  },

  async addUrl(presId, input) {
    const list = getList(presId);
    const id = 'res_' + Date.now().toString(36) + Math.random().toString(36).slice(2, 6);
    const url = normalizeHttpUrl(input.url);
    list.push({
      id,
      type: 'url',
      title: String(input.title || url),
      description: String(input.description || '') || undefined,
      url,
      isVisible: input.isVisible === true,
      order: list.length,
      createdAt: Date.now(),
      updatedAt: Date.now(),
      createdBy: 'stub-user'
    });
    setList(presId, list);
  },

  async addFile(presId, file, input) {
    const list = getList(presId);
    const id = 'res_' + Date.now().toString(36) + Math.random().toString(36).slice(2, 6);
    list.push({
      id,
      type: 'file',
      title: String(input?.title || file.name),
      description: String(input?.description || '') || undefined,
      url: '#',
      fileName: file.name,
      mimeType: file.type,
      sizeBytes: file.size,
      isVisible: input?.isVisible === true,
      order: list.length,
      createdAt: Date.now(),
      updatedAt: Date.now(),
      createdBy: 'stub-user'
    });
    setList(presId, list);
  },

  async updateResource(presId, resourceId, patch) {
    const list = getList(presId).map((r) =>
      r.id === resourceId
        ? {
            ...r,
            ...patch,
            updatedAt: Date.now()
          }
        : r
    );
    setList(presId, list);
  },

  async removeResource(presId, resourceId) {
    const list = getList(presId).filter((r) => r.id !== resourceId).map((r, i) => ({ ...r, order: i }));
    setList(presId, list);
  },

  async reorderResources(presId, orderedIds) {
    const byId = new Map(getList(presId).map((r) => [r.id, r] as const));
    const next: ResourceItem[] = [];
    orderedIds.forEach((id) => {
      const item = byId.get(id);
      if (item) next.push(item);
    });
    byId.forEach((item) => {
      if (!next.some((x) => x.id === item.id)) next.push(item);
    });
    setList(
      presId,
      next.map((r, i) => ({ ...r, order: i, updatedAt: Date.now() }))
    );
  }
};
